import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./CheckBoxRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ChoiceHandler } from "../../choices/BaseChoice";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { CheckBoxIcon } from "../../shared/CheckBoxIcon";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForInputFieldQuestion,
} from "../../../utils/WCAGHelper";
import useQuestion from "../../../hooks/useQuestion";
import { QuestionType } from "../../../models/enums/QuestionType";

interface Props extends RendererProps {
    choice: ChoiceModel;
    id?: string;
    name?: string;
    value?: string;
    onSelect?(): void;
    checked?: boolean;
    isDisabled?: boolean;
}

function CheckBoxRendererImpl(props: Props, ref: React.MutableRefObject<ChoiceHandler>) {
    const {
        choice,
        checked,
        gridRendering,
        onSelect,
        isDisabled,
        language,
        gridRenderedColumnIndex,
        gridRenderedRowIndex,
    } = props;

    const [hover, setHover] = React.useState(false);
    const theme = useTheme();
    const classes = useStyles({ theme });

    React.useImperativeHandle(ref, () => ({
        select() {
            onSelect();
        },
    }));

    if (!gridRendering) {
        return (
            <CheckBoxIcon checked={checked} disabled={isDisabled} backgroundless={gridRendering} />
        );
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const question = useQuestion();
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const useAriaLabelledBy = useAriaLabelledByForInputFieldQuestion(question);

    // Default should be aria-label if not grid rendered and aria-labelled by for variable type in grid
    const useAriaLabel = !useAriaLabelledBy && !gridRendering;
    const ariaAttributes = {};
    const isChoiceOrHybridGridType =
        questionType === QuestionType.ChoiceGrid ||
        questionType === QuestionType.ImageChoiceGrid ||
        questionType === QuestionType.HybridGrid;

    // For hybrid grid both choice and column will exist
    const choiceOrColumnHasIndexExist = gridRenderedColumnIndex || (choice && choice.index >= 0);

    if (useAriaLabelledBy && isChoiceOrHybridGridType) {
        if (choiceOrColumnHasIndexExist) {
            ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
                question,
                questionId,
                `${questionType.toString()}_CHOICEOPTION`,
                (gridRenderedColumnIndex ?? 0) + 1,
                gridRenderedRowIndex + 1,
                (choice.index ?? 0) + 1, // In grid question we have sub-question type so required column level index and choice level index
            );
        }
    } else if (useAriaLabel) {
        const choiceAriaText = removeHtmlTags(translate(choice, "TEXT", language));
        if (choiceAriaText) {
            ariaAttributes["aria-label"] = choiceAriaText;
        }
    }
    return (
        <div
            id={`${choice.elementId}_INPUT`}
            role="checkbox"
            aria-checked={checked}
            {...ariaAttributes}
            tabIndex={0}
            onKeyPress={(event) => {
                if (event.key === " ") {
                    onSelect();
                    event.preventDefault();
                }
            }}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={() => onSelect()}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classnames(
                classes.checkBox,
                gridRendering && classes.checkBoxGridCell,
                gridRendering && checked && classes.checkBoxGridCellChecked,
            )}
        >
            <CheckBoxIcon
                checked={checked}
                disabled={isDisabled}
                hover={hover}
                backgroundless={gridRendering}
            />
        </div>
    );
}

const CheckBoxRenderer = React.forwardRef(CheckBoxRendererImpl);

export default CheckBoxRenderer;
